import React, { useEffect, useState } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
} from "react-router-dom";
import AuthGate from "./auth/AuthGate";
import Content from "./Content";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider, useDispatch } from "react-redux";
import Tenants from "./pages/tenants/Tenants";
import { store } from "./redux/store";
import "./general.scss";
import { getUser } from "./services/user";
import { setUser } from "./redux/features/user";
import { getActionsPermissions } from "./services/actions";
import { setErrorFetchingActions } from "./redux/features/errorFetchingActions";
import { useTranslation } from "react-i18next";
import { getTenant, removeTenant } from "./utils/tenant";
import { ToastContainer } from "react-toastify";
import "react-multi-email/dist/style.css";
import { Center, Loader, MantineProvider } from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/dates/styles.css";
import { rootTheme } from "./theme";
import {
  changeLanguage,
  setCurrentActions,
  useSelectLanguage,
} from "./redux/features/settings/settingsSlice";
import {
  setServerActions,
  setTenancy,
} from "./redux/features/permissions/permissionsSlice";

const queryClient = new QueryClient();

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const lg = useSelectLanguage();
  const { i18n } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(lg, () => console.log(`i18n changed to: ${lg}`));
  }, [lg, i18n]);

  useEffect(() => {
    userActionsHandler();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function userActionsHandler() {
    try {
      const user = await getUser();
      dispatch(setUser(user));
      dispatch(setTenancy(user.tenantPermissionType));
      const userLNG = user?.tenant?.language || "en-US";
      dispatch(changeLanguage(userLNG));
    } catch (err) {
      console.error(err);
    }

    try {
      const res = await getActionsPermissions();
      dispatch(setCurrentActions(res));
      dispatch(setServerActions(res));
      setLoading(false);
    } catch (err) {
      console.error(err);
      dispatch(setErrorFetchingActions(true));
      navigate("/error");
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <div className="app">
      <Content />
    </div>
  );
}

export default function Root() {
  const { href } = window.location;
  const tenant = getTenant();
  if (href.includes("tenant=false")) {
    const newUrl = href.split("?tenant=false").join("");
    removeTenant();
    window.location = newUrl;
  }

  return (
    <Router>
      <MantineProvider theme={rootTheme}>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <AuthGate tenant={tenant}>
              <App />
            </AuthGate>
          </Provider>
          <Routes>
            <Route path="/select-tenant" Component={Tenants} />
          </Routes>
        </QueryClientProvider>
      </MantineProvider>

      <ToastContainer
        draggable={false}
        position="bottom-right"
        theme="colored"
        autoClose={10000}
        closeOnClick={false}
      />
    </Router>
  );
}
